import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { Search, Package, AlertTriangle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchProducts, fetchInitialData, Product, InitialData } from "@/data/productsData";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from 'use-debounce';
import { Helmet } from "react-helmet-async";

const ITEMS_PER_PAGE = 9;

/**
 * Google's own image CDN (lh3.googleusercontent.com) can serve a resized
 * version of an image on the fly via a size suffix — no processing needed
 * on our side. Only applies to Google-hosted URLs; anything else (our own
 * /assets, /catalog.png) passes through unchanged.
 * Width-only, no crop: lets the browser's own object-fit (cover/contain)
 * decide how to fit it — appending "-c" would instead have Google crop to
 * a square server-side, which can clip off-center or non-square photos
 * before the browser ever gets a say.
 */
const getSizedGoogleImage = (url: string | null | undefined, width: number): string => {
  if (!url || !url.includes('googleusercontent.com')) return url || '';
  const base = url.replace(/=[\w-]+$/, '');
  return `${base}=w${width}`;
};

/**
 * Renders a product photo with a progressive loading state:
 *  - While the image is fetching, shows a subtle placeholder icon instead of
 *    empty/blank space, so the card's text content never has to wait on it.
 *  - Fades the real photo in once it's loaded (no layout shift, no pop-in).
 *  - Falls back to the placeholder icon (instead of a broken-image icon) if
 *    the source fails to load — common with some Google Drive image links.
 * Defined outside of Products so its identity is stable across re-renders
 * (an inline component would remount on every parent render).
 */
const ProductImage = ({ src, alt }: { src: string | null; alt: string }) => {
  const sizedSrc = getSizedGoogleImage(src, 400);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(sizedSrc ? "loading" : "error");

  if (!sizedSrc || status === "error") {
    return (
      <div className="bg-secondary rounded-lg h-48 flex items-center justify-center mb-4">
        <Package className="h-16 w-16 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative bg-secondary rounded-lg h-48 mb-4 overflow-hidden">
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Package className="h-16 w-16 text-muted-foreground/40 animate-pulse" />
        </div>
      )}
      <img
        src={sizedSrc}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        className={`h-full w-full object-cover rounded-lg transition-opacity duration-300 ${
          status === "loaded" ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

const Products = () => {
  const { language, t, getLocalizedPath } = useLanguage();

  const pageTitle = `${t('products.title')} | KM KIT`;
  const pageDescription = t('products.subtitle');

  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  // The URL is the single source of truth for these two filters (not
  // useState) so that every way of changing them behaves the same:
  // picking a value in the dropdowns below, clicking a category/manufacturer
  // link on a product card, arriving from an external link
  // (/products?type=...), or using the browser back/forward buttons.
  const selectedManufacturer = searchParams.get("manufacturer") || "all";
  const selectedType = searchParams.get("type") || "all";

  const setSelectedManufacturer = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("manufacturer");
    else next.set("manufacturer", value);
    setSearchParams(next);
  };

  const setSelectedType = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("type");
    else next.set("type", value);
    setSearchParams(next);
  };

  const [isPreCacheEnabled, setPreCacheEnabled] = useState(false);

  // --- QUERY 1: Fetch initial data (random products + filter metadata) ---
  const { 
    data: initialData, 
    isLoading: isInitialLoading, 
    isError: isInitialError,
    refetch: refetchInitialData,
  } = useQuery<InitialData>({
    queryKey: ['products', 'initialData'],
    queryFn: fetchInitialData,
    staleTime: 3 * 60 * 1000, // Corresponds to the 3-min CDN cache
    refetchOnWindowFocus: false,
    retry: 1, // one retry max — a slow/failed attempt shouldn't compound into 2-3x the wait
  });

  // The backend responds 200 with an empty catalog + degraded:true instead of
  // a 500 when it genuinely fails (e.g. Google Sheets was unreachable) — this
  // keeps react-query's own retry logic from piling up multiple slow attempts.
  // But that also means react-query sees it as a *successful* fetch: nothing
  // here would ever retry on its own. Treat degraded the same as a hard error
  // for UI purposes, and give it exactly one automatic retry after a short
  // delay (covers a transient cold-start hiccup) before falling back to a
  // manual "try again" button.
  const hasLoadError = isInitialError || initialData?.degraded === true;
  const hasAutoRetried = useRef(false);

  useEffect(() => {
    if (hasLoadError && !hasAutoRetried.current) {
      hasAutoRetried.current = true;
      const timer = setTimeout(() => {
        refetchInitialData();
      }, 4000);
      return () => clearTimeout(timer);
    }
    if (!hasLoadError) {
      hasAutoRetried.current = false;
    }
  }, [hasLoadError, refetchInitialData]);

  // --- QUERY 2: Pre-cache all products in the background ---
  const { data: allProductsData, isSuccess: isPreCacheSuccess } = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => fetchProducts({ limit: 10000, page: 2 }), // Fetch all products
    enabled: isPreCacheEnabled, // Disabled by default
    staleTime: Infinity, // Keep this cached forever on the client
    refetchOnWindowFocus: false,
  });

  // Effect to trigger the background pre-caching after the initial data has been loaded
  useEffect(() => {
    if (initialData) {
      setPreCacheEnabled(true);
    }
  }, [initialData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, selectedManufacturer, selectedType]);

  // --- DATA HANDLING ---
  const { randomProducts, filterMetadata } = initialData || {};

  const filteredProducts = useMemo(() => {
    if (!isPreCacheSuccess || !allProductsData) return [];

    return allProductsData.products.filter(product => {
      const searchStr = debouncedSearchQuery.toLowerCase();
      const matchesSearch = !debouncedSearchQuery ||
                            product.name_source.toLowerCase().includes(searchStr) ||
                            product.description_source.toLowerCase().includes(searchStr) ||
                            (product['name_en'] || '').toLowerCase().includes(searchStr) ||
                            (product['name_de'] || '').toLowerCase().includes(searchStr) ||
                            (product['name_es'] || '').toLowerCase().includes(searchStr);

      const matchesManufacturer = selectedManufacturer === 'all' || product.manufacturer === selectedManufacturer;
      const matchesType = selectedType === 'all' || product.type === selectedType;

      return matchesSearch && matchesManufacturer && matchesType;
    });
  }, [allProductsData, isPreCacheSuccess, debouncedSearchQuery, selectedManufacturer, selectedType]);

  const paginatedProducts = useMemo(() => {
    const startIndex = currentPage <= 1 ? 0 : (currentPage - 2) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // CORRECTED LOGIC: Determine which data source to use
  const isInitialView = currentPage === 1 && !debouncedSearchQuery && selectedManufacturer === 'all' && selectedType === 'all';
  const useClientSideData = isPreCacheSuccess && !isInitialView;

  const products = useClientSideData ? paginatedProducts : (randomProducts?.products || []);
  const totalProducts = useClientSideData ? filteredProducts.length : (randomProducts?.totalProducts || 0);
  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  const isLoading = isInitialLoading;

  const manufacturers = useMemo(() => ["all", ...(filterMetadata?.manufacturers || [])], [filterMetadata]);
  const types = useMemo(() => ["all", ...(filterMetadata?.types || [])], [filterMetadata]);


  const paginationItems = useMemo(() => {
    const pages: (number | string)[] = [];
    const showEllipsisStart = currentPage > 3;
    const showEllipsisEnd = currentPage < totalPages - 2;

    pages.push(1);
    if (showEllipsisStart) {
      pages.push('ellipsis-start');
    }
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    if (showEllipsisEnd) {
      pages.push('ellipsis-end');
    }
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages.map((page, index) => {
      if (typeof page === 'string') {
        return (
          <PaginationItem key={`${page}-${index}`}>
            <span className="px-2 text-muted-foreground">...</span>
          </PaginationItem>
        );
      }
      return (
        <PaginationItem key={page}>
          <Button
            variant={currentPage === page ? "default" : "outline"}
            size="sm"
            onClick={() => setCurrentPage(page)}
            className={`h-9 w-9 p-0 ${currentPage === page ? 'bg-primary text-primary-foreground' : ''}`}
          >
            {page}
          </Button>
        </PaginationItem>
      );
    });
  }, [currentPage, totalPages]);

  const renderProductGrid = () => {
    if (isLoading) {
      return Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
        <Card key={index}><CardContent className="p-6"><Skeleton className="h-48 w-full mb-4" /><Skeleton className="h-6 w-3/4 mb-2" /><Skeleton className="h-4 w-1/4 mb-4" /><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-full mb-4" /><Skeleton className="h-6 w-1/4" /></CardContent><CardFooter className="p-6 pt-0"><Skeleton className="h-10 w-full" /></CardFooter></Card>
      ));
    }

    if (hasLoadError) {
      return (
        <div className="text-center py-12 col-span-full">
          <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">{t('products.loadingError')}</h3>
          <p className="text-muted-foreground mb-4">{t('products.errorMessage')}</p>
          <Button onClick={() => refetchInitialData()}>{t('products.tryAgain')}</Button>
        </div>
      );
    }
    
    if (products.length > 0) {
      return products.map((product) => {
        const productName = product['name_' + language] || product.name_source || t('product.comingSoon');
        return (
          <Card key={product.id} className="border transition-shadow hover:shadow-md">
            <CardContent className="p-6">
              <ProductImage src={product.image} alt={productName} />
              <h3 className="text-xl font-semibold mb-2">{productName}</h3>
              {product.manufacturer ? (
                <Link
                  to={getLocalizedPath(`/products?manufacturer=${encodeURIComponent(product.manufacturer)}`)}
                  className="inline-block text-sm text-primary font-medium mb-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {product.manufacturer}
                </Link>
              ) : (
                <div className="text-sm text-primary font-medium mb-2">{t('product.comingSoon')}</div>
              )}
              <p className="text-sm text-muted-foreground mb-4 h-20 overflow-hidden">{product['description_' + language] || product.description_source || t('product.comingSoon')}</p>
              {product.type ? (
                <Link
                  to={getLocalizedPath(`/products?type=${encodeURIComponent(product.type)}`)}
                  className="inline-block px-2 py-1 bg-secondary rounded text-xs font-medium hover:bg-secondary/70 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {product.type}
                </Link>
              ) : (
                <div className="inline-block px-2 py-1 bg-secondary rounded text-xs font-medium">{t('product.comingSoon')}</div>
              )}
            </CardContent>
            <CardFooter className="p-6 pt-0"><Button asChild className="w-full bg-accent hover:bg-accent/90"><Link to={getLocalizedPath(`/products/${product.id}`)}>{t('products.viewDetails')}</Link></Button></CardFooter>
          </Card>
        );
      });
    }

    if (debouncedSearchQuery) {
      return (<div className="col-span-full md:col-span-1 md:col-start-2 lg:col-start-2 flex justify-center"><Card className="border transition-shadow hover:shadow-md w-full max-w-sm"><CardContent className="p-6">          <div className="bg-secondary rounded-lg h-48 flex items-center justify-center mb-4">
            <Package className="h-16 w-16 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">"{debouncedSearchQuery}" <span className="font-normal text-muted-foreground">{t('products.noProducts')}</span></h3>
          <p className="text-sm text-muted-foreground mb-4 h-20 overflow-hidden">{t('products.noProductsHint')}</p></CardContent><CardFooter className="p-6 pt-0"><Button asChild className="w-full"><Link to={getLocalizedPath('/products/not-found')}>{t('products.requestProduct')}</Link></Button></CardFooter></Card></div>);
    }

    return (<div className="text-center py-12 col-span-full"><Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" /><h3 className="text-xl font-semibold mb-2">{t('products.noProducts')}</h3><p className="text-muted-foreground">{t('products.noProductsHint')}</p></div>);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${t('products.keywords')}, KM KIT, ${selectedManufacturer !== 'all' ? selectedManufacturer : ''}, ${selectedType !== 'all' ? selectedType : ''}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.kmkit.com${getLocalizedPath('/products')}`} />
      </Helmet>
      <Header />
      <main className="flex-1 py-12">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('products.title')}</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">{t('products.subtitle')}</p>
          </div>
          <div className="mb-8 space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input placeholder={t('products.searchPlaceholder')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" disabled={isLoading} />
              </div>
              <Select value={selectedManufacturer} onValueChange={setSelectedManufacturer} disabled={isLoading || !filterMetadata}>
                <SelectTrigger className="w-full md:w-[200px]"><SelectValue placeholder={t('products.allManufacturers')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.allManufacturers')}</SelectItem>
                  {manufacturers.slice(1).map((manufacturer) => (<SelectItem key={manufacturer} value={manufacturer}>{manufacturer}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={selectedType} onValueChange={setSelectedType} disabled={isLoading || !filterMetadata}>
                <SelectTrigger className="w-full md:w-[200px]"><SelectValue placeholder={t('products.allTypes')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.allTypes')}</SelectItem>
                  {types.slice(1).map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              {!isLoading && !hasLoadError && `${t('products.showing')} ${products.length} ${t('products.of')} ${totalProducts} ${t('products.products')}`}
              {isLoading && t('products.loading')}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[600px]">
            {renderProductGrid()}
          </div>
          {!isLoading && !hasLoadError && totalPages > 1 && (
            <div className="mt-12 flex flex-col items-center gap-4">
              <p className="text-sm text-muted-foreground">{t('products.page')} {currentPage} {t('products.of')} {totalPages}</p>
              <Pagination>
                <PaginationContent className="flex-wrap justify-center gap-1">
                  <PaginationItem><Button variant="outline" size="sm" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="h-9 px-3">{t('previous')}</Button></PaginationItem>
                  {paginationItems}
                  <PaginationItem><Button variant="outline" size="sm" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="h-9 px-3">{t('next')}</Button></PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Products;